import * as THREE from 'three'

export type XRScenePose = { position: THREE.Vector3Like; quaternion: THREE.QuaternionLike }
export type XRHandPoses = { left?: { target: XRScenePose; grip?: XRScenePose }; right?: { target: XRScenePose; grip?: XRScenePose } }

/** Local geometry keeps controllers/rays available without an external model CDN. */
export class XRHandRig {
  readonly rightGrip = new THREE.Group()
  readonly leftGrip = new THREE.Group()
  readonly rightRay = new THREE.Group()
  readonly leftRay = new THREE.Group()
  rightTracked = false
  leftTracked = false

  constructor(scene: THREE.Scene) {
    for (const [side, grip, ray, color] of [
      ['right', this.rightGrip, this.rightRay, 0xb4ffe0], ['left', this.leftGrip, this.leftRay, 0x75cfff],
    ] as const) {
      grip.name = `threecube-${side}-grip`; ray.name = `threecube-${side}-ray`
      const shell = new THREE.Mesh(new THREE.CapsuleGeometry(0.021, 0.07, 6, 12), new THREE.MeshBasicMaterial({ color: 0xe3ebe8, depthTest: false }))
      shell.position.y = -0.035; shell.rotation.x = 0.22; shell.renderOrder = 10920
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.032, 0.004, 8, 28), new THREE.MeshBasicMaterial({ color, depthTest: false }))
      ring.position.set(0, 0.023, -0.006); ring.rotation.x = -0.7; ring.renderOrder = 10921
      grip.add(shell, ring)
      const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.0014, 0.0014, 3, 8), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.72, depthTest: false, depthWrite: false }))
      beam.rotation.x = Math.PI / 2; beam.position.z = -1.5; beam.renderOrder = 10930
      const start = new THREE.Mesh(new THREE.SphereGeometry(0.004, 8, 8), new THREE.MeshBasicMaterial({ color, depthTest: false }))
      start.renderOrder = 10931
      ray.add(beam, start)
      scene.add(grip, ray)
    }
    this.hide()
  }

  update(poses: XRHandPoses) {
    const assign = (group: THREE.Group, pose?: XRScenePose) => {
      group.visible = !!pose
      if (pose) { group.position.copy(pose.position); group.quaternion.copy(pose.quaternion); group.updateMatrixWorld(true) }
    }
    this.rightTracked = !!poses.right
    this.leftTracked = !!poses.left
    assign(this.rightGrip, poses.right?.grip ?? poses.right?.target)
    assign(this.leftGrip, poses.left?.grip ?? poses.left?.target)
    assign(this.rightRay, poses.right?.target)
    assign(this.leftRay, poses.left?.target)
  }

  hide() { this.update({}) }

  dispose() {
    // Attached UI owns its own resources; only dispose our local controller models.
    for (const group of [this.rightGrip, this.leftGrip, this.rightRay, this.leftRay]) {
      for (const child of group.children) if (child instanceof THREE.Mesh && child.renderOrder >= 10920 && child.renderOrder <= 10931) { child.geometry.dispose(); (child.material as THREE.Material).dispose() }
      group.removeFromParent()
    }
  }
}

/** Retain a controller-local anchor while keeping the display facing the user's eyes. */
export function faceHandPanel(panel: THREE.Object3D, camera: THREE.Camera) {
  if (!panel.parent) return
  panel.updateWorldMatrix(true, false)
  const position = panel.getWorldPosition(new THREE.Vector3())
  const eye = camera.getWorldPosition(new THREE.Vector3())
  const orientation = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().lookAt(eye, position, new THREE.Vector3(0, 1, 0)))
  panel.quaternion.copy(panel.parent.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(orientation))
  panel.updateMatrixWorld(true)
}
